import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	exchangeAfasOneTimePassword,
	IntegrationFlowAfasAuthenticationResultKind,
	requestAfasOneTimePassword
} from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";

export type AfasAuthenticateResult = {
	authenticate: (
		authenticationId: number,
		onIsBusyChange?: (isBusy: boolean) => void
	) => Promise<boolean>;
	afasOtpDialog: ReactElement | null;
};

export function useAfasAuthenticate(): AfasAuthenticateResult {
	const [afasOtpDialog, openAfasOtpDialog] = useAfasOtpDialog();
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const authenticateHandler = useCallback(
		async (
			authenticationId: number,
			onIsBusyChange?: (isBusy: boolean) => void
		): Promise<boolean> => {
			try {
				onIsBusyChange?.(true);

				const requestOtpResult = await requestAfasOneTimePassword(
					authenticationId,
					globalCancellation
				);

				onIsBusyChange?.(false);

				switch (requestOtpResult.kind) {
					case IntegrationFlowAfasAuthenticationResultKind.RequestCreated:
						const otpDialogResult: TryResult<void> = await openAfasOtpDialog({
							authenticationId
						});

						return otpDialogResult.wasSuccessful;
					case IntegrationFlowAfasAuthenticationResultKind.RequireEmailAddress:
						const { isAdmin } = userConfiguration();

						alertManager.showError(
							localize(isAdmin
								? "_AfasEmailRequiredToActivateMessageForAdmin"
								: "_AfasEmailRequiredToActivateMessage"
							)
						);
						return false;
					default:
						never(requestOtpResult.kind);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return false;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				onIsBusyChange?.(false);
				return false;
			}
		},
		[
			alertManager,
			globalCancellation,
			openAfasOtpDialog
		]
	);

	return useMemo(
		(): AfasAuthenticateResult => ({
			authenticate: authenticateHandler,
			afasOtpDialog
		}),
		[authenticateHandler, afasOtpDialog]
	);
}

type AfasOtpDialogProps = {
	authenticationId: number;
	onClose: () => void;
	onAuthenticated: () => void;
};

const AfasOtpDialog = (props: AfasOtpDialogProps): ReactElement => {
	const {
		authenticationId,
		onClose,
		onAuthenticated
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);

	const [formData, setFormData] = useState<FormModel>({
		oneTimePassword: ""
	});

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler
					.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				await exchangeAfasOneTimePassword(
					authenticationId,
					formData,
					globalCancellation
				);

				onAuthenticated();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
				setIsBusy(false);
			}
		},
		[
			alertManager,
			authenticationId,
			formData,
			formHandler,
			globalCancellation,
			onAuthenticated
		]
	);

	return (
		<Dialog
			trackingId="afas-otp-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_EnterActivationCode")}
			/>
			<Dialog.Body>
				<p>
					{localize("_AfasActivationCodeIsSentToEmailHint")}
				</p>
				<Form onSubmit={submitHandler}>
					<FormItem label={localize("_ActivationCode")}>
						<TextInput
							{...formHandler.for("oneTimePassword").attachToTextInput()}
							placeholder={localize("_EnterActivationCode")}
							autoFocus="focus"
						/>

						<ValidationError {...formHandler.for("oneTimePassword").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						trackingId="activate"
						kind="primary"
						text={localize("_Activate")}
						onClick={submitHandler}
					/>
					<Toolbar.Button
						trackingId="cancel"
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
AfasOtpDialog.displayName = "AfasOtpDialog";

type FormModel = {
	oneTimePassword: string;
};

const validationConfiguration: IValidationConfiguration<FormModel> = createValidationConfiguration<FormModel>();

validationConfiguration
	.forScalar("oneTimePassword")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

type AfasOtpDialogInitialProps = Omit<AfasOtpDialogProps, "onClose" | "onAuthenticated">;

function useAfasOtpDialog(): [
	element: ReactElement | null,
	open: (props: AfasOtpDialogInitialProps) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onAuthenticated) => (
			<AfasOtpDialog
				{...props}
				onClose={onClose}
				onAuthenticated={onAuthenticated}
			/>
		)
	);
}